import { Component, OnInit, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, inject, Input, Output, EventEmitter, SimpleChanges, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { TreeTableModule } from 'primeng/treetable';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { RippleModule } from 'primeng/ripple';
import { DrawerModule } from 'primeng/drawer';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { GetModelsHierarchyResult } from '@generated/models';
import { LatestBenchmarkSnapshotDto } from '@generated/models/latest-benchmark-snapshot-dto';
import { ModelTesterComponent, TesterSelection } from '../model-tester/model-tester.component';
import {
  ModelPropertiesDrawerComponent,
  ModelPropertiesEditRequest
} from '@features/admin/model-properties-drawer/model-properties-drawer.component';
import { descriptorEntriesToDict } from '@shared/utils/openapi-schema-utils';
import { OpenApiPropertiesSchema } from '@generated/models/open-api-properties-schema';
import { AgentModelVersionDto } from '@generated/models/agent-model-version-dto';

/** Данные узла tree table.
 *  - `type` root: имя типа модели ("LLM", "STT", ...) + иконка.
 *  - `gateway` middle: имя шлюза + модель type code (для иконки).
 *  - `model` leaf: полный ModelRow — рендерим со всеми полями (автор,
 *    цены, benchmark snapshot).
 *  kind — discriminator, чтобы template не гадал по structural проверкам. */
interface ModelTreeData {
  kind: 'type' | 'gateway' | 'model';
  // Общее — для отображения имени/иконки
  label?: string;
  modelTypeCode?: string;
  // Только для model leaves
  row?: ModelRow;
}

interface VersionEntry {
  versionId: string;
  versionName: string;
  description?: string | null;
  additionalProperties?: any | null;
  /** Backend's per-version `is_recommended` — drives the star icon, row
   *  sort and the toggle inside the admin properties drawer. */
  isRecommended: boolean;
  // Pricing (per-version now; admin endpoint returns raw, public returns
  // user-coefficient-adjusted numbers — the source URL decides which).
  inputPricePerMTokens?: number | null;
  inputCachedPricePerMTokens?: number | null;
  inputCacheWritePricePerMTokens?: number | null;
  outputPricePerMTokens?: number | null;
  pricePerMCharacters?: number | null;
  pricePerMinute?: number | null;
  /** Snapshot последнего успешного BenchmarkRun'а для этой версии.
   *  Один объект держит и LLM (avg_ttft_ms, avg_tokens_per_sec), и STT
   *  (wer, cer, avg_rtfx) метрики — разбираем по model_type_code. Null
   *  когда версию ещё не бенчили. Раньше здесь были отдельные
   *  lastBenchmark* поля; после SKN-290 источник — общий Benchmark. */
  latestBenchmark?: LatestBenchmarkSnapshotDto | null;
}

export interface BenchmarkRequest {
  modelId: string;
  modelName: string;
  modelTypeCode: string;
  /** Guid типа модели. Прокидывается в llm-benchmark-drawer чтобы там
   *  подтянуть совместимые бенчи через GET /admin/benchmarks?modelTypeId=. */
  modelTypeId?: string | null;
  versions: VersionEntry[];
  // Version the user clicked on (when emitted from a version row) or
  // null when the click happened on the model row itself — the drawer
  // then offers a picker.
  preselectedVersionId: string | null;
  /** Optional список привязанных к строке бенчей. Если задан — drawer
   *  использует его и НЕ дёргает /admin/benchmarks. Полезно когда
   *  caller уже знает состав (например резолвил его при открытии
   *  чтобы сразу показать в UI). */
  benchmarkIds?: string[];
}

interface ModelRow {
  modelId: string;
  modelName: string;
  authorId: string;
  authorName: string;
  gatewayId: string;
  gatewayName: string;
  /** Stable gateway code (e.g. "openai", "custom") used to decide
   *  whether the tester drawer should switch to custom-LLM mode. */
  gatewayCode: string;
  modelTypeCode: string;
  /** Guid типа модели — используется чтобы фильтровать бенчмарки в
   *  model-properties-drawer через GET /admin/benchmarks?modelTypeId=. */
  modelTypeId: string;
  isRecommended: boolean;
  /** Где физически хостится модель (регион датацентра). Используется
   *  для показа маленького badge'а рядом с именем модели, чтобы юзер
   *  видел, что данные не уходят за пределы указанного региона. Null
   *  для моделей из include_version_snapshots — snapshot не несёт
   *  hosting_locale, бэк отдаёт его только в иерархии. */
  hostingLocale: { code: string; name: string; flag: string | null } | null;
  versions: VersionEntry[];
}

interface TestContext {
  modelTypeCode: string;
  versionId: string;
  versions: { id: string; name: string; additionalProperties?: any | null }[];
  versionLocked: boolean;
  additionalProperties?: any | null;
  currentValues?: Record<string, any> | null;
  currentVoiceId?: string | null;
  currentTemperature?: number | null;
  currentTopK?: number | null;
  currentTopP?: number | null;
  currentMaxOutputTokens?: number | null;
  /** True when the row's gateway code is in the caller's
   *  `customLlmGatewayCodes` list. Drives the tester's custom-LLM
   *  schema editors. */
  allowCustomLlm?: boolean;
  currentCustomHeadersSchema?: import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null;
  currentCustomBodySchema?: import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null;
  headerLine: string;
  subHeader: string;
}

@Component({
  selector: 'app-model-selector',
  standalone: true,
  imports: [
    FormsModule,
    TableModule,
    TreeTableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    TagModule,
    RippleModule,
    DrawerModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    ModelTesterComponent,
    ModelPropertiesDrawerComponent
],
  providers: [ConfirmationService],
  templateUrl: './model-selector.html',
  styleUrl: './model-selector.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModelSelector implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private confirmationService = inject(ConfirmationService);

  /** Список model_type_code для фильтра — 'llm'/'stt'/'tts'/'vad' и т.п.
   *  Строка, а не union, чтобы можно было прокидывать динамические значения
   *  (напр. benchmark-edit подставляет modelTypeCode() сигнала). */
  @Input() modelTypes: string[] = [];
  @Input() modelsHierarchy: GetModelsHierarchyResult | null = null;
  /** Parent сейчас перекачивает hierarchy — рисуем spinner-overlay. */
  @Input() hierarchyLoading = false;
  @Input() multiSelect: boolean = false;
  @Input() allowModelSelect: boolean = false;
  /**
   * Render the version-expansion chevron + per-version rows. Off by default
   * — version picking now lives inside the tester drawer's dropdown.
   */
  @Input() showVersions: boolean = false;
  @Input() set selectedVersionIds(value: string[]) {
    this._initialSelectedIds = value || [];
    this._selectedIds = new Set(this._initialSelectedIds);
  }
  /**
   * Version ids the table should hide entirely — used by the agent
   * fallback list to prevent picking the same model twice. Models that
   * end up with zero non-excluded versions disappear from the list, so
   * the tester drawer also never sees them. */
  @Input() set excludeVersionIds(value: string[] | null | undefined) {
    this._excludeIds = new Set(value || []);
    if (this.modelsHierarchy) this.rebuild();
  }
  get excludeVersionIds(): string[] {
    return Array.from(this._excludeIds);
  }
  @Input() defaultFirstMessage?: string | null;
  @Input() languageId?: string | null;
  @Input() localeId?: string | null;
  /**
   * If true, the tester drawer for each model renders a big "Select"
   * button that picks whichever version is currently shown in the tester
   * — handy when the user wants to try a few before committing without
   * closing the drawer to click Select in the row.
   */
  @Input() allowSelectFromTester: boolean = false;
  /**
   * Caller's currently-saved additional-property values for the active
   * selection. Forwarded to the tester so reopening the drawer for the
   * already-picked model restores the user's tweaks instead of re-seeding
   * from schema defaults.
   */
  @Input() currentAdditionalProperties?: Record<string, any> | null;
  /** Caller's currently-saved TTS voice id; pre-selects it in the picker. */
  @Input() currentVoiceId?: string | null;
  /** Caller's currently-saved LLM temperature; forwarded into the tester
   *  so reopening for the already-picked LLM row restores the slider. */
  @Input() currentTemperature?: number | null;
  @Input() currentTopK?: number | null;
  @Input() currentTopP?: number | null;
  @Input() currentMaxOutputTokens?: number | null;
  /** Multi-select контексты (benchmark-edit): per-version overrides.
   *  Приоритетнее чем currentTemperature/… — открывая tester для конкретной
   *  версии, подсовываем именно её сохранённые sampling params и
   *  additional_properties. */
  @Input() versionSamplingParams?: Record<string, {
    temperature?: number | null;
    topK?: number | null;
    topP?: number | null;
    maxOutputTokens?: number | null;
    additionalProperties?: Record<string, any> | null;
  }> | null;
  /** Custom LLM gateway codes (e.g. ['custom']) — when the picked row's
   *  gateway code is in this list, the tester drawer renders the custom
   *  headers/body schema editors. */
  @Input() customLlmGatewayCodes: string[] = [];
  /** Initial schemas for the custom-LLM editors. Forwarded as-is when
   *  the row's gateway matches `customLlmGatewayCodes`. */
  @Input() currentCustomHeadersSchema?: import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null;
  @Input() currentCustomBodySchema?: import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null;
  /**
   * Server-side "show all models, not just recommended". The parent owns
   * the hierarchy fetch, so this is just the current toggle state; we
   * emit `includeAllChange` and the parent re-issues the request.
   * Default false = only recommended (matches the regular user UI);
   * admin pages override to true.
   */
  @Input() set includeAll(value: boolean) {
    this._includeAll = value;
    // Локальная UI-модель свитча = обратная от includeAll. Sync здесь
    // (при изменении @Input снаружи), чтобы свитч следовал за parent-стейтом.
    this.recommendedOnlyModel = !value;
  }
  get includeAll(): boolean { return this._includeAll; }
  private _includeAll = false;

  /** Скрыть селектор моделей — только tester остаётся видимым. Используется
   *  когда caller уже знает какую модель тестить (autoOpenTesterVersionId)
   *  и хочет прямо перейти в тестер, без экрана выбора. */
  @Input() disableModelSelection = false;
  /** Version id для авто-открытия tester'а на mount'е. Работает только если
   *  версия существует в текущей иерархии моделей. */
  @Input() autoOpenTesterVersionId?: string;

  /**
   * Дополнительные модели/версии, которые надо показать в селекторе
   * ПОМИМО тех что пришли в `modelsHierarchy`. Кейс: у агента сохранена
   * модель, которую бэк уже не отдаёт (deprecated, not_recommended при
   * includeAll=false, retired) — без inject'а пользователь видит пустой
   * селектор при том что кнопка показывает валидное имя.
   *
   * Принимаем snapshot'ы `AgentModelVersionDto` (та же shape что
   * agent.model_versions[i].model_version) — из них берём id, name,
   * model, author, gateway. Тип модели наследуем от `modelTypes[0]`
   * (caller передаёт snapshot'ы отфильтрованными под свой picker).
   */
  @Input() includeVersionSnapshots?: AgentModelVersionDto[] | null;

  /** UI-модель для p-toggleSwitch — привязана через [(ngModel)]. Отдельно
   *  от includeAll, потому что PrimeNG v21 p-toggleSwitch self-update'ит
   *  визуальное состояние на клик, а parent-input `!includeAll` мы
   *  меняем только после подтверждения confirm-dialog'а. При reject
   *  вручную возвращаем этот field в исходное состояние — Angular
   *  push'ит writeValue обратно, свитч визуально возвращается. */
  recommendedOnlyModel = true;

  @Output() selectionConfirmed = new EventEmitter<string | string[]>();
  @Output() cancelled = new EventEmitter<void>();
  /** Пробрасывается в model-tester → показывает admin-only кнопку
   *  «Edit properties». Дефолт false; админские страницы явно передают true. */
  @Input() showPropertiesEdit = false;
  /** New value of `includeAll` after the user toggled (and accepted the
   *  confirmation when turning the "Recommended only" switch off). */
  @Output() includeAllChange = new EventEmitter<boolean>();
  /**
   * Fires when the user clicks "Select" inside the tester drawer. Unlike
   * `selectionConfirmed` (versionId only), this carries the voice id and
   * the additional-property values the user tweaked in the tester so the
   * parent can apply them to the agent in one go.
   */
  @Output() testerSelected = new EventEmitter<TesterSelection>();

  private _initialSelectedIds: string[] = [];
  private _selectedIds: Set<string> = new Set();
  private _excludeIds: Set<string> = new Set();

  testDrawerVisible = false;
  testContext: TestContext | null = null;

  /** Admin properties drawer mounts here так работает для любого caller'а —
   *  видимость кнопки в тестере регулируется через [showPropertiesEdit]. */
  propertiesDrawerVisible = false;
  propertiesRequest: ModelPropertiesEditRequest | null = null;

  /** Live handle to the active tester inside the drawer — needed so the
   *  drawer footer Select button can ask it to commit. */
  @ViewChild(ModelTesterComponent) private testerRef?: ModelTesterComponent;

  allRows: ModelRow[] = [];
  filteredRows: ModelRow[] = [];
  /** Дерево для p-treeTable: root — тип модели (LLM/STT/TTS/VAD),
   *  дети — шлюзы, листья — сами модели (ModelRow). Пересобирается в
   *  applyFilters() после каждого изменения фильтров, чтобы show только
   *  подходящие узлы. Все уровни expanded по умолчанию — юзер сразу
   *  видит все модели без клика по toggler'у. */
  filteredTree: TreeNode<ModelTreeData>[] = [];

  /**
   * Virtual scroll резервирует одинаковый vertical slot (virtualScrollItemSize=90)
   * под КАЖДЫЙ visible node — включая type-row и gateway-row, у которых
   * реального контента 30-40px против 90px под model-row с pricing/bench.
   * При малом количестве узлов заголовочные строки визуально "провисают"
   * пустым space'ом. Порог поднят до 20: под большинство фильтр-выборок
   * (1 gateway, 3-10 моделей) virtual scroll выключен, строки берут
   * natural-height и type/gateway-headers не растягиваются до 90px. */
  get useVirtualScroll(): boolean {
    return this.countTreeNodes(this.filteredTree) > 20;
  }
  private countTreeNodes(nodes: TreeNode[]): number {
    let n = 0;
    for (const node of nodes) {
      n++;
      if (node.children?.length) n += this.countTreeNodes(node.children);
    }
    return n;
  }

  gatewayFilter: string = '';
  authorFilter: string = '';
  modelNameFilter: string = '';
  selectedOnlyFilter: boolean = false;
  modelTypeFilter: string = '';

  gatewayOptions: { label: string; value: string }[] = [];
  authorOptions: { label: string; value: string }[] = [];
  modelTypeOptions: { label: string; value: string }[] = [];

  readonly selectedFilterOptions = [
    { label: $localize`:@@modelSelector.all:All`, value: false },
    { label: $localize`:@@modelSelector.selectedOnly:Selected Only`, value: true }
  ];
  readonly recommendedLabel = $localize`:@@modelSelector.recommended:Recommended`;
  readonly selectLabel = $localize`:@@modelSelector.select:Select`;
  readonly deselectLabel = $localize`:@@modelSelector.deselect:Deselect`;

  /** Полный текст для тултипа над hosting-locale тегом. Собираем в TS,
   *  а не $localize с параметрами, ради простоты interp — юзер видит
   *  «Model is hosted in <название региона> (<код>). Data processed
   *  by this model stays within this region.». */
  hostingLocaleTooltip(hl: { code: string; name: string; flag: string | null }): string {
    const prefix = $localize`:@@modelSelector.hostingLocale.prefix:Model is hosted in`;
    const suffix = $localize`:@@modelSelector.hostingLocale.suffix:Data processed by this model stays within this region.`;
    return `${prefix} ${hl.name} (${hl.code}). ${suffix}`;
  }

  /** Флаг: auto-open уже отработал (либо потому что нашли версию, либо
   *  потому что hierarchy не приезжает). Не даём повторно открывать tester
   *  при последующих rebuild'ах (напр. когда юзер сам его закрыл). */
  private autoOpenDone = false;

  ngOnInit() {
    this.rebuild();
    this.tryAutoOpenTester();
  }

  /** Public: находит row по id версии и открывает tester через
   *  openTesterForModel — там корректно проходят currentVoiceId,
   *  currentAdditionalProperties и остальные prefills. openTesterForVersion
   *  используется только когда юзер сам кликнул конкретную версию из
   *  таблицы, там prefills не нужны. */
  openTesterForVersionId(versionId: string): boolean {
    for (const row of this.allRows) {
      const version = row.versions.find(v => v.versionId === versionId);
      if (version) {
        // Гарантируем что openTesterForModel'овский savedId — сойдётся
        // на нужной версии (там ищется по _initialSelectedIds). Это
        // критично для доп-локалей: без этого row.versions[0] может
        // оказаться не той версией, которую агент сохранил.
        if (!this._initialSelectedIds.includes(versionId)) {
          this._initialSelectedIds = [versionId, ...this._initialSelectedIds];
        }
        this.openTesterForModel(row);
        return true;
      }
    }
    return false;
  }

  private tryAutoOpenTester(): void {
    if (this.autoOpenDone) return;
    const versionId = this.autoOpenTesterVersionId;
    if (!versionId) return;
    // Hierarchy может быть ещё не пришедшей — allRows пуст. Тогда молча
    // выходим, попробуем ещё раз на следующем rebuild (см. ngOnChanges).
    if (this.allRows.length === 0) return;
    // queueMicrotask даёт Angular время достроить DOM для tester-drawer'а
    // до того как мы дёрнем testDrawerVisible=true.
    queueMicrotask(() => {
      if (this.autoOpenDone) return;
      const opened = this.openTesterForVersionId(versionId);
      if (opened) this.autoOpenDone = true;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reactive = ['modelsHierarchy', 'modelTypes', 'showVersions', 'allowModelSelect', 'excludeVersionIds'];
    if (reactive.some(k => changes[k] && !changes[k].firstChange)) {
      this.rebuild();
      // Hierarchy наконец-то приехала → повторим попытку авто-открытия.
      this.tryAutoOpenTester();
    }
  }

  private rebuild() {
    this.buildModelRows();
    this.buildGatewayOptions();
    this.buildAuthorOptions();
    this.buildModelTypeOptions();
    this.applyFilters();
  }

  isLlmRow(row: ModelRow): boolean {
    return (row.modelTypeCode || '').toLowerCase() === 'llm';
  }

  isSttRow(row: ModelRow): boolean {
    return (row.modelTypeCode || '').toLowerCase() === 'stt';
  }

  isTtsRow(row: ModelRow): boolean {
    return (row.modelTypeCode || '').toLowerCase() === 'tts';
  }

  /**
   * Pricing now lives on the version. The row shows the *latest* version's
   * rates as a compact indicator — backend returns versions newest-first
   * already, so versions[0] is the right pick. VAD rows have no pricing.
   */
  latestVersionForRow(row: ModelRow): VersionEntry | null {
    return row.versions[0] ?? null;
  }

  /** Render the pricing block only when there's at least one non-zero rate
   *  for the row's model type. 0 is treated as "no price" so admins can
   *  hide rates without juggling nulls. */
  hasPricingToShow(row: ModelRow, latest: VersionEntry): boolean {
    if (this.isLlmRow(row)) return !!(latest.inputPricePerMTokens || latest.outputPricePerMTokens);
    if (this.isTtsRow(row)) return !!latest.pricePerMCharacters;
    if (this.isSttRow(row)) return !!latest.pricePerMinute;
    return false;
  }

  formatPerMTokens(value: number | null | undefined): string {
    return value == null ? '—' : `$${value.toFixed(2)}/Mt`;
  }

  formatPerMCharacters(value: number | null | undefined): string {
    return value == null ? '—' : `$${value.toFixed(2)}/Mc`;
  }

  formatPerMinute(value: number | null | undefined): string {
    return value == null ? '—' : `$${value.toFixed(4)}/min`;
  }

  /**
   * Best-effort summary of benchmark metrics for the model row — picks the
   * most recently benchmarked version across all versions of the model.
   * Один бейдж для двух типов моделей (LLM/STT): row может смешивать версии
   * разных типов только теоретически (в реальности modelType задаётся на
   * уровне модели), но мы всё равно сортируем по recorded_at, чтобы
   * приоритизировать свежий прогон.
   */
  bestBenchmarkForRow(row: ModelRow): VersionEntry | null {
    let best: VersionEntry | null = null;
    for (const v of row.versions) {
      if (!v.latestBenchmark) continue;
      if (!best || !best.latestBenchmark) { best = v; continue; }
      const a = best.latestBenchmark.recorded_at ? Date.parse(best.latestBenchmark.recorded_at) : 0;
      const b = v.latestBenchmark.recorded_at ? Date.parse(v.latestBenchmark.recorded_at) : 0;
      if (b > a) best = v;
    }
    return best;
  }

  formatTokensPerSec(value: number | null | undefined): string {
    return value == null ? '—' : `${value.toFixed(1)} t/s`;
  }

  formatTtftMs(value: number | null | undefined): string {
    return value == null ? '—' : `${Math.round(value)} ms`;
  }

  /** WER/CER — доля 0..1; показываем в процентах с одним знаком после
   *  запятой. Null → прочерк. */
  formatErrorRate(value: number | null | undefined): string {
    return value == null ? '—' : `${(value * 100).toFixed(1)}%`;
  }

  /** RTFx = скорость обработки относительно real-time (2× → 2 raz'а
   *  быстрее чем длительность аудио). */
  formatRtfx(value: number | null | undefined): string {
    return value == null ? '—' : `${value.toFixed(2)}×`;
  }

  formatBenchmarkAt(value: string | null | undefined): string {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }

  /** Тип модели у которой был последний бенч — 'llm' | 'stt' | ''.
   *  Ниже в шаблоне выбираем набор метрик под этот тип. */
  bestBenchmarkTypeCode(row: ModelRow): string {
    const best = this.bestBenchmarkForRow(row);
    return (best?.latestBenchmark?.model_type_code || '').toLowerCase();
  }

  /**
   * Apply a fresh benchmark snapshot to the version row in-place — the
   * parent calls this after a live `BenchmarkRunUpdated` event so the
   * table reflects the new metrics without reloading the hierarchy.
   */
  applyBenchmarkUpdate(versionId: string, snapshot: Partial<LatestBenchmarkSnapshotDto>): void {
    for (const row of this.allRows) {
      const v = row.versions.find(x => x.versionId === versionId);
      if (!v) continue;
      // Мержим свежие поля в существующий snapshot; если раньше latest
      // был null (версию бенчили впервые) — собираем новый объект.
      v.latestBenchmark = { ...(v.latestBenchmark ?? {} as LatestBenchmarkSnapshotDto), ...snapshot };
      this.cdr.markForCheck();
      return;
    }
  }

  private buildModelRows() {
    this.allRows = [];
    if (!this.modelsHierarchy?.model_types) {
      this.mergeIncludeSnapshots();
      return;
    }

    const targetModelTypes = this.modelsHierarchy.model_types.filter(
      mt => this.modelTypes.length === 0 ||
            this.modelTypes.some(type => mt.code?.toLowerCase() === type.toLowerCase())
    );

    targetModelTypes.forEach(modelTypeData => {
      modelTypeData.gateways?.forEach(gateway => {
        gateway.models?.forEach(model => {
          const modelId = model.id || '';
          const versions: VersionEntry[] = (model.versions || [])
            // Drop versions the caller has explicitly excluded — e.g. an
            // agent's primary LLM doesn't get to reappear as a fallback.
            .filter(v => !this._excludeIds.has(v.id || ''))
            .map(v => {
              const versionId = v.id || '';
              return {
                versionId,
                versionName: v.name || '',
                description: v.description,
                // SKN-313: additional_properties больше не приходят в hierarchy —
                // tester сам грузит их через GET /api/Models/versions/{id}.
                additionalProperties: null,
                isRecommended: v.is_recommended ?? false,
                inputPricePerMTokens: v.input_price_per_m_tokens ?? null,
                inputCachedPricePerMTokens: v.input_cached_price_per_m_tokens ?? null,
                inputCacheWritePricePerMTokens: v.input_cache_write_price_per_m_tokens ?? null,
                outputPricePerMTokens: v.output_price_per_m_tokens ?? null,
                pricePerMCharacters: v.price_per_m_characters ?? null,
                pricePerMinute: v.price_per_minute ?? null,
                latestBenchmark: v.latest_benchmark ?? null
              };
            })
            .sort((a, b) => Number(b.isRecommended) - Number(a.isRecommended));
          // Model is entirely useless when every version was excluded —
          // skip the row so the tester drawer can't open an empty list.
          if (versions.length === 0) return;
          const isRecommended = versions.some(v => v.isRecommended);
          const hl = model.hosting_locale;
          this.allRows.push({
            modelId,
            modelName: model.name || '',
            authorId: model.author?.id || '',
            authorName: model.author?.name || '',
            gatewayId: gateway.id || '',
            gatewayName: gateway.name || '',
            gatewayCode: gateway.code || '',
            modelTypeCode: modelTypeData.code || '',
            modelTypeId: modelTypeData.id || '',
            isRecommended,
            hostingLocale: hl?.code
              ? { code: hl.code, name: hl.name || hl.code, flag: hl.flag ?? null }
              : null,
            versions
          });
        });
      });
    });

    this.mergeIncludeSnapshots();
  }

  /** Инъекция моделей/версий, которые бэк не отдал в иерархии, но у
   *  caller'а сохранены на агенте. Дедуп по version.id: если версия
   *  уже в allRows — ничего не делаем; если модель есть но версии нет
   *  — дописываем версию в существующий row; иначе создаём новый row. */
  private mergeIncludeSnapshots(): void {
    if (!this.includeVersionSnapshots?.length) return;
    const excluded = this._excludeIds;
    const fallbackTypeCode = (this.modelTypes[0] || '').toLowerCase();
    for (const snap of this.includeVersionSnapshots) {
      const versionId = snap?.id;
      if (!versionId || excluded.has(versionId)) continue;
      // Уже есть в иерархии — пропускаем.
      if (this.allRows.some(r => r.versions.some(v => v.versionId === versionId))) continue;

      const version: VersionEntry = {
        versionId,
        versionName: snap.name || snap.version || '',
        description: snap.description ?? null,
        additionalProperties: null,
        isRecommended: snap.is_recommended ?? false,
        inputPricePerMTokens: null,
        inputCachedPricePerMTokens: null,
        inputCacheWritePricePerMTokens: null,
        outputPricePerMTokens: null,
        pricePerMCharacters: null,
        pricePerMinute: null,
        latestBenchmark: null,
      };

      const modelId = snap.model?.id || '';
      const existingModelRow = modelId
        ? this.allRows.find(r => r.modelId === modelId)
        : undefined;
      if (existingModelRow) {
        existingModelRow.versions.push(version);
        continue;
      }
      this.allRows.push({
        modelId,
        modelName: snap.model?.name || '',
        authorId: snap.author?.id || '',
        authorName: snap.author?.name || '',
        gatewayId: snap.gateway?.id || '',
        gatewayName: snap.gateway?.name || '',
        gatewayCode: snap.gateway?.code || '',
        modelTypeCode: fallbackTypeCode,
        modelTypeId: '',
        isRecommended: version.isRecommended,
        hostingLocale: null,
        versions: [version],
      });
    }
  }

  private buildGatewayOptions() {
    const unique = new Map<string, string>();
    this.allRows.forEach(row => {
      if (!unique.has(row.gatewayId)) unique.set(row.gatewayId, row.gatewayName);
    });
    if (unique.size === 1) {
      const [[id, name]] = Array.from(unique.entries());
      this.gatewayOptions = [{ label: name, value: id }];
      this.gatewayFilter = id;
    } else {
      this.gatewayOptions = [
        { label: $localize`:@@modelSelector.allGateways:All Gateways`, value: '' },
        ...Array.from(unique.entries()).map(([id, name]) => ({ label: name, value: id }))
      ];
    }
  }

  private buildAuthorOptions() {
    const unique = new Map<string, string>();
    this.allRows.forEach(row => {
      if (row.authorId && !unique.has(row.authorId)) unique.set(row.authorId, row.authorName);
    });
    if (unique.size === 1) {
      const [[id, name]] = Array.from(unique.entries());
      this.authorOptions = [{ label: name, value: id }];
      this.authorFilter = id;
    } else {
      this.authorOptions = [
        { label: $localize`:@@modelSelector.allAuthors:All Authors`, value: '' },
        ...Array.from(unique.entries()).map(([id, name]) => ({ label: name, value: id }))
      ];
    }
  }

  private buildModelTypeOptions() {
    const unique = new Map<string, string>();
    this.allRows.forEach(row => {
      if (row.modelTypeCode && !unique.has(row.modelTypeCode)) {
        const mt = this.modelsHierarchy?.model_types?.find(t => t.code === row.modelTypeCode);
        if (mt) unique.set(row.modelTypeCode, mt.name || mt.code || '');
      }
    });
    if (unique.size === 1) {
      const [[code, name]] = Array.from(unique.entries());
      this.modelTypeOptions = [{ label: name, value: code }];
      this.modelTypeFilter = code;
    } else {
      this.modelTypeOptions = [
        { label: $localize`:@@modelSelector.allTypes:All Types`, value: '' },
        ...Array.from(unique.entries()).map(([code, name]) => ({ label: name, value: code }))
      ];
    }
  }

  /**
   * The toggle reads "Only recommended" — its checked state mirrors the
   * NEGATION of `includeAll`. Turning it OFF (i.e., trying to reveal the
   * full catalog) is the risky transition: surface a confirmation that
   * explains the trade-off before letting the request go out. Turning it
   * back ON (returning to the curated subset) is always safe — fire away.
   */
  onRecommendedOnlyToggle(newRecommendedOnly: boolean): void {
    const newIncludeAll = !newRecommendedOnly;
    if (newIncludeAll === this.includeAll) return;

    if (newIncludeAll) {
      this.confirmationService.confirm({
        header: $localize`:@@modelSelector.includeAll.confirm.header:Open the full model catalog?`,
        message: $localize`:@@modelSelector.includeAll.confirm.message:Right now you see the models we curate and regularly verify — we watch their quality, stability and performance. The full catalog includes many other models: we don't validate their parameters and don't guarantee they work. Use them at your own discretion.`,
        acceptLabel: $localize`:@@common.continue:Continue`,
        rejectLabel: $localize`:@@common.cancel:Cancel`,
        acceptButtonProps: { severity: 'warn' },
        rejectButtonProps: { severity: 'secondary', outlined: true },
        accept: () => {
          this.includeAll = true;
          // Switching from the curated subset to the full catalog often
          // surfaces gateways/authors that weren't available before. If
          // the previous filters auto-pinned to a single option (sole-
          // option auto-select in buildGatewayOptions/buildAuthorOptions),
          // the user would otherwise see an inexplicably empty table —
          // reset both so the new catalog opens up.
          this.gatewayFilter = '';
          this.authorFilter = '';
          this.includeAllChange.emit(true);
          this.cdr.markForCheck();
        },
        reject: () => {
          // Юзер отказался — свитч успел визуально уйти в OFF (p-toggleSwitch
          // self-update'ит DOM на клик до нашего callback'а). Пишем ngModel
          // обратно в true; Angular push'ит writeValue → свитч возвращается.
          this.recommendedOnlyModel = true;
          this.cdr.markForCheck();
        }
      });
      return;
    }

    this.includeAll = false;
    this.includeAllChange.emit(false);
    this.cdr.markForCheck();
  }

  applyFilters() {
    const nameLc = this.modelNameFilter.toLowerCase();
    this.filteredRows = this.allRows.filter(row => {
      const gatewayMatch = !this.gatewayFilter || row.gatewayId === this.gatewayFilter;
      const authorMatch = !this.authorFilter || row.authorId === this.authorFilter;
      const nameMatch = !nameLc ||
        row.modelName.toLowerCase().includes(nameLc) ||
        row.authorName.toLowerCase().includes(nameLc) ||
        row.versions.some(v => v.versionName.toLowerCase().includes(nameLc));
      const selectedMatch = !this.selectedOnlyFilter ||
        this._selectedIds.has(row.modelId) ||
        row.versions.some(v => this._selectedIds.has(v.versionId));
      const typeMatch = !this.modelTypeFilter || row.modelTypeCode === this.modelTypeFilter;
      // "Recommended only" is server-side now (includeAll URL param) — the
      // parent refetches the hierarchy on toggle, so the rows we see here
      // are already the right set.
      return gatewayMatch && authorMatch && nameMatch && selectedMatch && typeMatch;
    });

    const isAnySelected = (row: ModelRow): boolean =>
      this._selectedIds.has(row.modelId) ||
      row.versions.some(v => this._selectedIds.has(v.versionId));

    this.filteredRows.sort((a, b) => {
      const aSel = isAnySelected(a);
      const bSel = isAnySelected(b);
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      if (a.isRecommended && !b.isRecommended) return -1;
      if (!a.isRecommended && b.isRecommended) return 1;
      return 0;
    });

    // Пересобираем tree из отфильтрованного плоского списка.
    this.filteredTree = this.buildTree(this.filteredRows);

    this.cdr.markForCheck();
  }

  /**
   * Собираем tree из уже отфильтрованного plaintext-массива:
   *   тип модели (root, expanded) → шлюз (mid, expanded) → модель (leaf).
   * Каждый уровень expanded=true, чтобы юзер сразу видел все модели без
   *   клика по toggler'у.
   * label узла типа — display-имя из modelsHierarchy.model_types, чтобы
   *   заголовок читаемый (не сырое "llm" / "stt"), с fallback на code.
   */
  private buildTree(rows: ModelRow[]): TreeNode<ModelTreeData>[] {
    // Group by modelTypeCode → gatewayId, сохраняя порядок появления
    // — allRows уже отсортирован под наши sort/фильтр правила.
    const byType = new Map<string, Map<string, ModelRow[]>>();
    for (const row of rows) {
      const t = row.modelTypeCode;
      const g = row.gatewayId;
      if (!byType.has(t)) byType.set(t, new Map());
      const gm = byType.get(t)!;
      if (!gm.has(g)) gm.set(g, []);
      gm.get(g)!.push(row);
    }

    const typeNameByCode = new Map<string, string>();
    for (const mt of this.modelsHierarchy?.model_types ?? []) {
      if (mt.code) typeNameByCode.set(mt.code, mt.name || mt.code);
    }

    const out: TreeNode<ModelTreeData>[] = [];
    for (const [typeCode, gateways] of byType) {
      const typeNode: TreeNode<ModelTreeData> = {
        key: `t:${typeCode}`,
        data: {
          kind: 'type',
          modelTypeCode: typeCode,
          label: typeNameByCode.get(typeCode) || typeCode.toUpperCase(),
        },
        expanded: true,
        children: [],
      };
      for (const [gwId, gwRows] of gateways) {
        const first = gwRows[0];
        const gwNode: TreeNode<ModelTreeData> = {
          key: `g:${typeCode}:${gwId}`,
          data: {
            kind: 'gateway',
            modelTypeCode: typeCode,
            label: first.gatewayName,
          },
          expanded: true,
          children: gwRows.map(row => ({
            key: `m:${row.modelId}`,
            data: {
              kind: 'model',
              modelTypeCode: row.modelTypeCode,
              row,
            },
            leaf: true,
          })),
        };
        typeNode.children!.push(gwNode);
      }
      out.push(typeNode);
    }
    return out;
  }

  // ===== selection =====
  selectModel(row: ModelRow) {
    if (this.multiSelect) {
      this.toggleId(row.modelId);
      return;
    }
    if (!this.allowModelSelect) return;
    this.selectionConfirmed.emit(row.modelId);
  }

  /**
   * Row click: единая логика для single- и multi-select — открываем
   * тестер, юзер выбирает версию + параметры + жмёт Select/Deselect в
   * футере. Так пользователь и в multiSelect может настроить доп-параметры
   * версии, а не просто накидать модели скопом. Раньше в multiSelect клик
   * тупо toggl'ил по modelId, что игнорировало версию и её настройки.
   */
  onRowClick(row: ModelRow): void {
    if (row.versions.length > 0) this.openTesterForModel(row);
  }

  /** Клик по строке tree-таблицы. Type/Gateway — работают как заголовки
   *  секций, ничего не делаем (юзер разворачивает/сворачивает через
   *  chevron treetoggler'а). Только Model-листья открывают тестер. */
  onTreeRowClick(data: ModelTreeData): void {
    if (data.kind !== 'model' || !data.row) return;
    this.onRowClick(data.row);
  }

  /** True, если конкретная версия входит в текущий multiSelect набор.
   *  Тестер использует это для лейбла и severity Select-кнопки в футере. */
  isVersionSelected(versionId: string | null | undefined): boolean {
    if (!versionId) return false;
    return this._selectedIds.has(versionId);
  }

  /** Toggle конкретной версии из тестерного footer'а. Используется в
   *  multiSelect режиме — вместо ассоциации с модельным ID щёлкаем именно
   *  ту версию, что сейчас активна в тестере. */
  toggleVersion(versionId: string): void {
    this.toggleId(versionId);
  }

  /** Быстрая кнопка Deselect в multiSelect строке: снимает всё, что было
   *  выбрано для этой модели — и сам modelId, и все versionIds. Не
   *  требует открывать тестер. */
  deselectRow(row: ModelRow): void {
    this._selectedIds.delete(row.modelId);
    for (const v of row.versions) this._selectedIds.delete(v.versionId);
    this.cdr.markForCheck();
  }

  /** Активная версия в тестерном drawer'е — тестер может переключить
   *  версию через свой dropdown, поэтому берём именно с него, а не с
   *  testContext.versionId (тот замерз на моменте открытия). */
  activeTesterVersionId(): string | null {
    return this.testerRef?.selectedTestVersionId ?? this.testContext?.versionId ?? null;
  }

  /** «Deselect this version» в тестерном footer'е: снимаем ТОЛЬКО активную
   *  версию, чтобы юзер мог гибко управлять составом набора не закрывая
   *  drawer. Emit наверх ModelSelectorButton не нужен — обёртка читает
   *  effectiveSelectedIds на закрытии диалога через confirmSelection(). */
  deselectActiveTesterVersion(): void {
    const versionId = this.activeTesterVersionId();
    if (!versionId) return;
    this._selectedIds.delete(versionId);
    this.cdr.markForCheck();
  }

  /**
   * Tester's admin "Edit properties" button — find the active version in
   * the row (the tester may have switched in its dropdown) and open the
   * drawer with a version-level snapshot. Schema + is_recommended + pricing
   * — единственное что редактируем per-version. Настройки самого бенчмарка
   * (system prompt / temperature / auto-run) ушли на уровень Benchmark
   * entity после SKN-290, здесь их больше нет.
   */
  onTesterPropertiesEditRequested(versionId: string): void {
    const row = this.allRows.find(r => r.versions.some(v => v.versionId === versionId));
    if (!row) return;
    const activeId = this.testerRef?.selectedTestVersionId ?? versionId;
    const version = row.versions.find(v => v.versionId === activeId)
      ?? row.versions.find(v => v.versionId === versionId);
    if (!version) return;
    this.propertiesRequest = {
      versionId: version.versionId,
      versionName: version.versionName,
      modelName: row.authorName ? `${row.authorName} ${row.modelName}` : row.modelName,
      modelTypeCode: row.modelTypeCode,
      modelTypeId: row.modelTypeId,
      additionalProperties: (version.additionalProperties as OpenApiPropertiesSchema | null) ?? null,
      isRecommended: version.isRecommended,
      inputPricePerMTokens: version.inputPricePerMTokens ?? null,
      inputCachedPricePerMTokens: version.inputCachedPricePerMTokens ?? null,
      inputCacheWritePricePerMTokens: version.inputCacheWritePricePerMTokens ?? null,
      outputPricePerMTokens: version.outputPricePerMTokens ?? null,
      pricePerMCharacters: version.pricePerMCharacters ?? null,
      pricePerMinute: version.pricePerMinute ?? null
    };
    this.propertiesDrawerVisible = true;
    this.cdr.markForCheck();
  }

  onPropertiesDrawerVisibleChange(visible: boolean): void {
    this.propertiesDrawerVisible = visible;
    if (!visible) this.propertiesRequest = null;
    this.cdr.markForCheck();
  }

  /** After admin saves version properties, mutate the row's version data
   *  in place AND push the fresh schema into the open tester's testContext
   *  so the inline OpenApi panel below the tester immediately shows the
   *  new fields ready for input — no reopen, no hierarchy reload. */
  onPropertiesSaved(payload: {
    versionId: string;
    additionalProperties: OpenApiPropertiesSchema | null;
    isRecommended: boolean;
    inputPricePerMTokens: number | null;
    inputCachedPricePerMTokens: number | null;
    inputCacheWritePricePerMTokens: number | null;
    outputPricePerMTokens: number | null;
    pricePerMCharacters: number | null;
    pricePerMinute: number | null;
  }): void {
    let row: ModelRow | undefined;
    let version: VersionEntry | undefined;
    for (const r of this.allRows) {
      const v = r.versions.find(x => x.versionId === payload.versionId);
      if (v) { row = r; version = v; break; }
    }
    if (!row || !version) return;
    version.additionalProperties = payload.additionalProperties;
    version.isRecommended = payload.isRecommended;
    version.inputPricePerMTokens = payload.inputPricePerMTokens;
    version.inputCachedPricePerMTokens = payload.inputCachedPricePerMTokens;
    version.inputCacheWritePricePerMTokens = payload.inputCacheWritePricePerMTokens;
    version.outputPricePerMTokens = payload.outputPricePerMTokens;
    version.pricePerMCharacters = payload.pricePerMCharacters;
    version.pricePerMinute = payload.pricePerMinute;

    if (this.testContext && row.versions.some(v => v.versionId === this.testContext!.versionId)) {
      const activeVersionId = this.testerRef?.selectedTestVersionId ?? this.testContext.versionId;
      const activeVersion = row.versions.find(v => v.versionId === activeVersionId);
      this.testContext = {
        ...this.testContext,
        versionId: activeVersionId,
        versions: this.buildVersionList(row),
        additionalProperties: this.versionSchemaDict(activeVersion?.additionalProperties)
      };
    }
    this.cdr.markForCheck();
  }

  selectVersion(_row: ModelRow, version: VersionEntry) {
    if (this.multiSelect) {
      this.toggleId(version.versionId);
    } else {
      this.selectionConfirmed.emit(version.versionId);
    }
  }

  /**
   * Tester-side "Select" commits the version, voice, and tweaked
   * additional properties picked in the tester drawer. We fire the rich
   * `testerSelected` event for parents that want voice/properties, then
   * fall back to the normal selectVersion path so existing consumers
   * that only listen to `selectionConfirmed` still get the version id.
   */
  onTesterVersionSelected(selection: TesterSelection): void {
    this.testerSelected.emit(selection);
    const row = this.allRows.find(r => r.versions.some(v => v.versionId === selection.versionId));
    const version = row?.versions.find(v => v.versionId === selection.versionId);
    if (row && version) {
      this.selectVersion(row, version);
    }
    this.closeTester();
  }

  /** Triggered by the Select button in the drawer footer — forwards to
   *  the live tester instance which emits versionSelected. */
  commitTesterSelection(): void {
    this.testerRef?.onSelectVersionClick();
  }

  /** Footer-button disabled state mirrors the tester's `canSelect`. */
  get canCommitTesterSelection(): boolean {
    return !!this.testerRef?.canSelect;
  }

  private toggleId(id: string) {
    if (this._selectedIds.has(id)) this._selectedIds.delete(id);
    else this._selectedIds.add(id);
    this.cdr.markForCheck();
  }

  // Подсветка строки/сортировка: выбрана сама модель ИЛИ любая её версия
  isModelSelected(row: ModelRow): boolean {
    if (this._selectedIds.has(row.modelId)) return true;
    return row.versions.some(v => this._selectedIds.has(v.versionId));
  }

  confirmSelection() {
    this.selectionConfirmed.emit(Array.from(this._selectedIds));
  }

  cancel() {
    // Откатываем выделение к исходному входу и сообщаем родителю — он спрячет селектор
    this._selectedIds = new Set(this._initialSelectedIds);
    this.cancelled.emit();
    this.cdr.markForCheck();
  }

  // ===== test =====
  /**
   * Version-level additional_properties → descriptor dict the tester's
   * app-properties-edit expects. Model-level schema no longer participates;
   * everything lives on the version now.
   */
  private versionSchemaDict(versionAp: any | null | undefined): Record<string, any> | null {
    const dict = descriptorEntriesToDict(versionAp?.properties);
    return Object.keys(dict).length > 0 ? dict : null;
  }

  private buildVersionList(row: ModelRow) {
    return row.versions.map(v => ({
      id: v.versionId,
      name: v.versionName,
      additionalProperties: this.versionSchemaDict(v.additionalProperties),
      latestBenchmark: v.latestBenchmark ?? null,
    }));
  }

  openTesterForModel(row: ModelRow): void {
    // Prefer the version that the caller currently has saved (so reopening
    // for the already-picked model lands on its version + its tweaks),
    // otherwise default to the first version.
    const savedId = this._initialSelectedIds.find(id => row.versions.some(v => v.versionId === id));
    const initial = savedId
      ? row.versions.find(v => v.versionId === savedId)!
      : row.versions[0];
    if (!initial) return;
    const isRestoredSelection = !!savedId;
    // Per-version sampling overrides — приоритетнее чем single "current*"
    // inputs. Актуально для multi-select контекстов (benchmark-edit),
    // где у каждой линковки свои значения.
    const perVer = this.versionSamplingParams?.[initial.versionId];
    this.testContext = {
      modelTypeCode: row.modelTypeCode,
      versionId: initial.versionId,
      versions: this.buildVersionList(row),
      versionLocked: row.versions.length === 1,
      additionalProperties: this.versionSchemaDict(initial.additionalProperties),
      // Saved tweaks belong to the already-picked version only. В multi-select
      // — приоритет за per-version'ом (versionSamplingParams[id].additionalProperties),
      // иначе fallback на общий currentAdditionalProperties.
      currentValues: perVer?.additionalProperties
        ?? (isRestoredSelection ? (this.currentAdditionalProperties ?? null) : null),
      currentVoiceId: isRestoredSelection ? (this.currentVoiceId ?? null) : null,
      currentTemperature: perVer?.temperature ?? (isRestoredSelection ? (this.currentTemperature ?? null) : undefined),
      currentTopK: perVer?.topK ?? (isRestoredSelection ? (this.currentTopK ?? null) : undefined),
      currentTopP: perVer?.topP ?? (isRestoredSelection ? (this.currentTopP ?? null) : undefined),
      currentMaxOutputTokens: perVer?.maxOutputTokens ?? (isRestoredSelection ? (this.currentMaxOutputTokens ?? null) : undefined),
      // Custom-LLM editors render only when the caller listed the row's
      // gateway code. Restored selections forward the saved schemas;
      // fresh picks start from empty schemas the user can fill in.
      allowCustomLlm: this.isCustomLlmGateway(row),
      currentCustomHeadersSchema: isRestoredSelection ? (this.currentCustomHeadersSchema ?? null) : null,
      currentCustomBodySchema: isRestoredSelection ? (this.currentCustomBodySchema ?? null) : null,
      headerLine: row.authorName ? `${row.authorName} ${row.modelName}` : row.modelName,
      subHeader: row.gatewayName
    };
    this.testDrawerVisible = true;
    this.cdr.markForCheck();
  }

  private isCustomLlmGateway(row: ModelRow): boolean {
    if (!this.customLlmGatewayCodes?.length) return false;
    return this.customLlmGatewayCodes.includes(row.gatewayCode);
  }

  openTesterForVersion(row: ModelRow, version: VersionEntry): void {
    this.testContext = {
      modelTypeCode: row.modelTypeCode,
      versionId: version.versionId,
      versions: this.buildVersionList(row),
      versionLocked: true,
      additionalProperties: this.versionSchemaDict(version.additionalProperties),
      headerLine: row.authorName ? `${row.authorName} ${row.modelName}` : row.modelName,
      subHeader: row.gatewayName
    };
    this.testDrawerVisible = true;
    this.cdr.markForCheck();
  }

  closeTester(): void {
    this.testDrawerVisible = false;
    this.testContext = null;
    // При disableModelSelection селектор без tester'а бесполезен —
    // эмитим cancelled, чтобы родитель схлопнул кнопку обратно.
    if (this.disableModelSelection) this.cancelled.emit();
    this.cdr.markForCheck();
  }

  // ===== meta =====
  getModelTypeIcon(modelTypeCode: string): string {
    const icons: Record<string, string> = {
      llm: 'pi-comments',
      stt: 'pi-microphone',
      tts: 'pi-volume-up',
      vad: 'pi-wave-pulse'
    };
    return icons[modelTypeCode.toLowerCase()] || 'pi-box';
  }

  getModelTypeTooltip(modelTypeCode: string): string {
    const tooltips: Record<string, string> = {
      llm: $localize`:@@modelSelector.type.llm:Language Model`,
      stt: $localize`:@@modelSelector.type.stt:Speech-to-Text`,
      tts: $localize`:@@modelSelector.type.tts:Text-to-Speech`,
      vad: $localize`:@@modelSelector.type.vad:Voice Activity Detection`
    };
    return tooltips[modelTypeCode.toLowerCase()] || modelTypeCode.toUpperCase();
  }
}
